import React from 'react';
import { theme } from 'antd';
import { motion } from 'framer-motion';

export interface ISidebarNavItem {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** Pinned to the end of the row -- an unread count, a warning dot. */
  trailing?: React.ReactNode;
}

export interface ISidebarNavListProps {
  items: ISidebarNavItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  /**
   * Must be unique on the page: framer-motion matches the sliding pill
   * between renders by this id, and two lists sharing one would have their
   * pills fly across the screen at each other.
   */
  pillLayoutId: string;
}

/**
 * The left-hand nav of a sidebar page -- Settings, the admin panel.
 *
 * The pill does not appear and disappear, it SLIDES: framer-motion's shared
 * layoutId means the same element is re-measured into its new row, so the
 * selection reads as one object moving rather than two states swapping. That
 * was built for Settings and lived there; the admin panel, which is the
 * screen the platform's own operators spend the day in, had a static grey
 * rectangle with a 10px radius instead.
 *
 * One component, so the next sidebar gets the good one without anybody
 * having to remember where the good one was.
 */
const SidebarNavList: React.FC<ISidebarNavListProps> = ({ items, activeKey, onSelect, pillLayoutId }) => {
  const { token } = theme.useToken();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {items.map((item) => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onSelect(item.key)}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              borderRadius: 999,
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              background: 'transparent',
              color: isActive ? token.colorText : token.colorTextSecondary,
              fontWeight: isActive ? 600 : 400,
              fontSize: 15,
              transition: 'color 150ms ease-out',
            }}
          >
            {isActive && (
              <motion.div
                layoutId={pillLayoutId}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 999,
                  background: token.colorFillTertiary,
                }}
              />
            )}
            <span style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
              {item.icon}
              {item.label}
            </span>
            {item.trailing && <span style={{ position: 'relative', flexShrink: 0 }}>{item.trailing}</span>}
          </button>
        );
      })}
    </div>
  );
};

export default SidebarNavList;
